import { Search } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { Kbd } from '@/components/kbd'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { overlays, useOpener } from '@/overlays/store'

/**
 * Route → header title. The header used to hardcode "Dashboard", so every page
 * in the app — reels, analyses, detail views — announced itself as the
 * dashboard. Longest matching prefix wins, so `/pages/new` beats `/pages`.
 */
const TITLES: [prefix: string, title: string][] = [
  ['/audiences', 'Audience'],
  ['/reels', 'Reels'],
  ['/social/new', 'New post'],
  ['/social', 'Social'],
  ['/pages/new', 'New page'],
  ['/pages', 'Pages'],
  ['/settings', 'Settings'],
  ['/', 'Health'],
]

// The table is injectable so a test can hand in a deliberately mis-ordered
// copy: with TITLES already declared longest-first, the sort would otherwise
// be unfalsifiable — removable without any test noticing.
export function titleFor(pathname: string, titles: typeof TITLES = TITLES): string {
  const match = titles
    .filter(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`))
    // Order by specificity rather than trusting the array order to also be a
    // precedence order.
    .sort((a, b) => b[0].length - a[0].length)[0]
  return match?.[1] ?? 'Warike'
}

export function SiteHeader() {
  const { pathname } = useLocation()

  return (
    <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
      <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
        {/* The collapse shortcut lives on the primitive (⌘B/Ctrl+B); the
            attribute hands a screen reader the same fact. The wordmark sits
            atop the inner menu, beside the rail's mark — not here. */}
        <SidebarTrigger className="-ml-1" aria-keyshortcuts="Meta+B Control+B" />
        <Separator orientation="vertical" className="mx-2 data-[orientation=vertical]:h-4" />
        <h1 className="text-base font-medium">{titleFor(pathname)}</h1>
        <div className="ml-auto flex items-center gap-2">
          <CommandTrigger />
        </div>
      </div>
    </header>
  )
}

/**
 * The palette's one visible affordance. A shortcut nobody can discover is a
 * shortcut nobody uses, so the chrome carries a button that both opens the
 * palette and teaches the keystroke — and `aria-keyshortcuts` gives a screen
 * reader the same fact the ⌘K chip gives a sighted user.
 *
 * A real `Button`, not a hand-classed one. It used to hand-roll its own border,
 * padding, hover and focus ring, which is how it ended up 32px tall next to a
 * 36px theme toggle — two adjacent controls in one header, neither agreeing with
 * the other. Going through the variant buys the shared press state and icon
 * motion for free, and there is now nothing left to drift.
 */
function CommandTrigger() {
  const opener = useOpener('palette')
  return (
    <Button
      variant="ghost"
      size="sm"
      {...opener}
      onClick={() => overlays.open('palette')}
      // The visible "Search" label and the ⌘K chip are both hidden below sm,
      // which would leave the button nameless exactly where it is icon-only.
      aria-label="Search"
      aria-keyshortcuts="Meta+K Control+K"
      // `group` so the chip can answer the BUTTON's hover. Doing it in Tailwind
      // rather than a CSS host selector keeps it from out-specifying the chip's
      // own key-press state, which also sets a colour.
      className="group text-muted-foreground"
    >
      {/* The glyph lights up and throws a soft halo — brighter on ink, deeper on
          paper. `.icon-glow` owns `filter`, so it must not be paired with
          `.icon-brighten`. */}
      <Search className="icon-glow size-4" aria-hidden />
      <span className="hidden sm:inline">Search</span>
      {/* Darker, not coloured: `--ring` is brand teal now, so borrowing it here
          would tint the chip. `foreground` at an alpha is the same neutral the
          chip already uses, one step up — which also means it goes LIGHTER on ink
          and DARKER on paper without needing a second rule. */}
      <Kbd className="hidden group-hover:border-foreground/30 group-hover:text-foreground sm:inline-flex">
        ⌘K
      </Kbd>
    </Button>
  )
}
